import React, { useEffect, useMemo, useRef, useState } from "react";
import Fuse, { FuseOptionKey, IFuseOptions } from "fuse.js";

type Styling = {
  height?: string;
  border?: string;
  borderRadius?: string;
  backgroundColor?: string;
  boxShadow?: string;
  hoverBackgroundColor?: string;
  color?: string;
  fontSize?: string;
  fontFamily?: string;
  iconColor?: string;
  lineColor?: string;
  placeholderColor?: string;
  clearIconMargin?: string;
  searchIconMargin?: string;
};

type Props<T extends Record<string, any>> = {
  items: T[];
  onSearch?: (query: string, results: T[]) => void;
  onHover?: (item: T) => void;
  onSelect?: (item: T) => void;
  onFocus?: () => void;
  formatResult?: (item: T) => React.ReactNode;

  inputSearchString?: string; // kontrollierter Wert (optional)
  showIcon?: boolean;
  autoFocus?: boolean;

  fuseOptions?: Omit<IFuseOptions<T>, "keys"> & { keys: FuseOptionKey<T>[] };
  resultStringKeyName?: keyof T; // z.B. "name"

  styling?: Styling;
  placeholder?: string;
};

export function LightSearchAutocomplete<T extends Record<string, any>>({
  items,
  onSearch,
  onHover,
  onSelect,
  onFocus,
  formatResult,
  inputSearchString,
  showIcon = false,
  autoFocus = false,
  fuseOptions,
  resultStringKeyName,
  styling,
  placeholder = "Suche …",
}: Props<T>) {
  // ----- State
  const [query, setQuery] = useState(inputSearchString ?? "");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  // ----- Controlled/Uncontrolled Sync
  useEffect(() => {
    if (typeof inputSearchString === "string") setQuery(inputSearchString);
  }, [inputSearchString]);

  // ----- Fuse Index
  const fuse = useMemo(() => {
    if (!fuseOptions) return null;
    return new Fuse(items, fuseOptions as IFuseOptions<T>);
  }, [items, fuseOptions]);

  // ----- Filter/Results
  const results: T[] = useMemo(() => {
    if (!query.trim()) return [];
    if (fuse) {
      return fuse.search(query).map(r => r.item);
    }
    // Simple Fallback-Filter:
    const key = resultStringKeyName as string | undefined;
    const q = query.toLowerCase();
    return items.filter(it => {
      if (key && typeof it[key] === "string") return (it[key] as string).toLowerCase().includes(q);
      // heuristisch alle string-Felder prüfen
      return Object.values(it).some(v => typeof v === "string" && v.toLowerCase().includes(q));
    });
  }, [query, fuse, items, resultStringKeyName]);

  useEffect(() => {
    onSearch?.(query, results);
  }, [query, results, onSearch]);

  // ----- Refs & A11y
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // ----- Keyboard
  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex(i => Math.min((i < 0 ? -1 : i) + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex(i => Math.max((i < 0 ? results.length : i) - 1, 0));
    } else if (e.key === "Enter") {
      if (open && activeIndex >= 0 && results[activeIndex]) {
        e.preventDefault();
        onSelect?.(results[activeIndex]);
        setOpen(false);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // ----- Styles (Defaults + Overrides)
  const S: Required<Styling> = {
    height: "30px",
    border: "1px solid rgb(206, 212, 218)",
    borderRadius: "0",
    backgroundColor: "white",
    boxShadow: "none",
    hoverBackgroundColor: "#eee",
    color: "#212121",
    fontSize: "14px",
    fontFamily: '"Open Sans", sans-serif',
    iconColor: "grey",
    lineColor: "rgb(232, 234, 237)",
    placeholderColor: "grey",
    clearIconMargin: "3px 5px 0 0",
    searchIconMargin: "0 0 0 5px",
    ...(styling ?? {}),
  };

  return (
    <div
      ref={rootRef}
      className="lsa"
      role="combobox"
      aria-expanded={open}
      aria-haspopup="listbox"
      aria-owns="lsa-listbox"
      style={{ position: "relative", width: "100%" }}
    >
      <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
        {showIcon && (
          <span
            aria-hidden
            style={{
              position: "absolute",
              left: 0,
              margin: S.searchIconMargin,
              color: S.iconColor,
              fontSize: S.fontSize,
              lineHeight: S.height,
            }}
          >
            🔍
          </span>
        )}

        <input
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); setActiveIndex(-1); }}
          onFocus={() => { setOpen(true); onFocus?.(); }}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          aria-autocomplete="list"
          aria-controls="lsa-listbox"
          style={{
            width: "100%",
            height: S.height,
            border: S.border,
            borderRadius: S.borderRadius,
            background: S.backgroundColor,
            boxShadow: S.boxShadow,
            color: S.color,
            fontSize: S.fontSize,
            fontFamily: S.fontFamily,
            padding: showIcon ? "0 28px 0 28px" : "0 8px",
            outline: "none",
          }}
          autoFocus={autoFocus}
        />

        {/* Clear-Icon */}
        {!!query && (
          <button
            type="button"
            onClick={() => { setQuery(""); setOpen(false); }}
            aria-label="Eingabe löschen"
            style={{
              position: "absolute",
              right: 0,
              margin: S.clearIconMargin,
              background: "transparent",
              border: 0,
              cursor: "pointer",
              color: S.iconColor,
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        )}
      </div>

      {open && results.length > 0 && (
        <ul
          id="lsa-listbox"
          role="listbox"
          style={{
            position: "absolute",
            zIndex: 20,
            top: `calc(${S.height} + 4px)`,
            left: 0,
            right: 0,
            maxHeight: 260,
            overflow: "auto",
            margin: 0,
            padding: 0,
            listStyle: "none",
            border: S.border,
            borderTop: `1px solid ${S.lineColor}`,
            background: "#fff",
          }}
        >
          {results.map((item, idx) => {
            const isActive = idx === activeIndex;
            // Versuche eine eindeutige ID zu finden, fallback auf idx
            const itemKey = item.id ?? item.mgNr ?? item.key ?? idx;
            return (
              <li
                key={itemKey}
                role="option"
                aria-selected={isActive}
                onMouseEnter={() => { setActiveIndex(idx); onHover?.(item); }}
                onMouseDown={(e) => e.preventDefault()} // verhindert Fokusverlust vor onClick
                onClick={() => { onSelect?.(item); setOpen(false); }}
                style={{
                  padding: "6px 10px",
                  cursor: "pointer",
                  background: isActive ? S.hoverBackgroundColor : "transparent",
                  borderBottom: `1px solid ${S.lineColor}`,
                }}
              >
                {formatResult
                  ? formatResult(item)
                  : (
                    <span>
                      {resultStringKeyName ? String(item[resultStringKeyName]) : JSON.stringify(item)}
                    </span>
                  )}
              </li>
            );
          })}
        </ul>
      )}

      {/* einfache Unterstreichung / Linie */}
      <div style={{ height: 1, background: S.lineColor, marginTop: 4 }} />
    </div>
  );
}
